import { ColourSwatchSelector } from '@/features/shared/components/ColourSwatchSelector';
import {
  colourSwatchSelectorControlSize,
  colourSwatchSelectorWidth,
} from '@/features/shared/components/colourSwatchSelectorMetrics';
import { projectBadgeColorOptions } from '@/shared/data/projectBadges';
import { r, s } from '@/theme/tokens';
import { Box, IconButton, TextField } from '@/ui/primitives';
import { DeleteFilledIcon as DeleteIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';

export const defaultTagColor: TagColor = 'neutral';

type TableRowTagEditorRowProps = {
  index: number;
  onColorChange: (index: number, color: TagColor) => void;
  onRemove: (index: number) => void;
  onTagChange: (index: number, value: string) => void;
  selectedColor: TagColor;
  tag: string;
};

export const TableRowTagEditorRow = ({
  index,
  onColorChange,
  onRemove,
  onTagChange,
  selectedColor,
  tag,
}: TableRowTagEditorRowProps): JSX.Element => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${colourSwatchSelectorWidth}`,
          alignItems: 'center',
          gap: s('md'),
        }}
      >
        <TextField
          size="small"
          inputHeight={32}
          value={tag}
          onChange={(event) => {
            onTagChange(index, event.target.value);
          }}
          fullWidth
          sx={(theme) => ({
            '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              minHeight: colourSwatchSelectorControlSize,
              height: colourSwatchSelectorControlSize,
            },
            '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
              fontSize: theme.typography.subtitle1.fontSize,
              lineHeight: theme.typography.subtitle1.lineHeight,
            },
          })}
        />
        <ColourSwatchSelector<TagColor>
          ariaLabel={`Choose colour for tag ${index + 1}`}
          value={selectedColor}
          options={projectBadgeColorOptions}
          onChange={(color) => {
            onColorChange(index, color);
          }}
        />
      </Box>
      <IconButton
        aria-label={`Remove tag ${index + 1}`}
        size="medium"
        sx={{
          width: `calc(${s('2xl')} + ${s('sm')})`,
          height: `calc(${s('2xl')} + ${s('sm')})`,
          minWidth: `calc(${s('2xl')} + ${s('sm')})`,
          minHeight: `calc(${s('2xl')} + ${s('sm')})`,
          p: s('none'),
          borderRadius: r('pill'),
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => {
          onRemove(index);
        }}
      >
        <DeleteIcon sx={{ color: 'error.main', fontSize: `calc(${s('2xl')} + ${s('xs')})` }} />
      </IconButton>
    </Box>
  );
};
